import { useState } from 'react'
import { CalendarDays, Building2, MapPin, CalendarRange, Users, AlertTriangle } from 'lucide-react'
import api from '../services/api'
import { useEmpresa } from '../contexts/EmpresaContext'
import { Linha } from './LancamentoDepositos'
import { SeletorMultiplo } from './RelatorioConsumoArla'

// Calendário de férias do ano: gozos lançados (passados e programados), pico de ausentes por mês, ausências acima do limite e prazo do aviso

interface Gozo { ini: string; fim: string; dias: number; programado: boolean; avisoAte: string | null; avisoAtrasado: boolean }
interface Func { motcod: string; nome: string; cargo: string; diasNoAno: number; gozos: Gozo[] }
interface Mes { mes: number; pico: number; acima: boolean; pessoas: number }
interface Conflito { grupo: string; ini: string; fim: string; pico: number; nomes: string[] }
interface Resultado { ano: number; limite: number; agrupar: 'cargo' | 'todos'; hoje: string; totalAtivos: number; funcionarios: Func[]; meses: Mes[]; conflitos: Conflito[] }

const MESES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']
const dataBr = (iso: string) => (iso ? iso.split('-').reverse().join('/') : '')
const diaDoMes = (iso: string) => Number(iso.slice(8, 10))
const ultimoDia = (ano: number, mes: number) => new Date(ano, mes, 0).getDate()

// trechos de um gozo dentro de um mês, em % da largura da célula
function trecho(g: Gozo, ano: number, mes: number): { left: number; width: number } | null {
  const mm = String(mes).padStart(2, '0')
  const total = ultimoDia(ano, mes)
  const iniMes = `${ano}-${mm}-01`, fimMes = `${ano}-${mm}-${String(total).padStart(2, '0')}`
  if (g.fim < iniMes || g.ini > fimMes) return null
  const de = g.ini < iniMes ? 1 : diaDoMes(g.ini)
  const ate = g.fim > fimMes ? total : diaDoMes(g.fim)
  return { left: ((de - 1) / total) * 100, width: ((ate - de + 1) / total) * 100 }
}

export default function CalendarioFerias() {
  const { empresas, empresaCod } = useEmpresa()
  const anoAtual = new Date().getFullYear()
  const [empresasSel, setEmpresasSel] = useState<string[]>([])
  const [filial, setFilial] = useState('')
  const [ano, setAno] = useState(anoAtual)
  const [limite, setLimite] = useState(2)
  const [agrupar, setAgrupar] = useState<'cargo' | 'todos'>('cargo')
  const [res, setRes] = useState<Resultado | null>(null)
  const [carregando, setCarregando] = useState(false)
  const [erro, setErro] = useState('')

  async function gerar() {
    setErro('')
    setCarregando(true)
    try {
      const r = await api.get('/funcionarios/relatorio/calendario-ferias', { params: { empresas: empresasSel.join(','), filial, ano, limite, agrupar, empresa: empresaCod ?? 1 } })
      setRes(r.data)
    } catch (e: any) {
      setRes(null)
      setErro(e?.response?.data?.error || 'Erro ao gerar o calendário.')
    } finally {
      setCarregando(false)
    }
  }

  async function abrirAviso(motcod: string, ini: string) {
    setErro('')
    try {
      const r = await api.get(`/funcionarios/${encodeURIComponent(motcod)}/ferias/aviso`, { params: { ini }, responseType: 'blob' })
      window.open(URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' })), '_blank')
    } catch (e: any) {
      let msg = 'Erro ao gerar o aviso de férias.'
      try { if (e?.response?.data instanceof Blob) msg = JSON.parse(await e.response.data.text()).error || msg } catch {}
      setErro(msg)
    }
  }

  const th: React.CSSProperties = { padding: '0.5rem 0.6rem', fontSize: '0.68rem', fontWeight: 800, color: '#fff', background: '#1b3f7b', textAlign: 'left', whiteSpace: 'nowrap', textTransform: 'uppercase' }
  const td: React.CSSProperties = { padding: '0.4rem 0.6rem', fontSize: '0.82rem', borderBottom: '1px solid #eef2f7', textAlign: 'left' }
  const chip = (fg: string, bg: string): React.CSSProperties => ({ padding: '0.3rem 0.8rem', borderRadius: 8, fontSize: '0.78rem', fontWeight: 700, color: fg, background: bg })

  const programadas = res ? res.funcionarios.flatMap(f => f.gozos.filter(g => g.programado).map(g => ({ f, g }))).sort((a, b) => a.g.ini.localeCompare(b.g.ini)) : []
  const atrasados = programadas.filter(x => x.g.avisoAtrasado).length
  const marcaHoje = res && res.hoje.startsWith(String(res.ano)) ? { mes: Number(res.hoje.slice(5, 7)), pct: ((diaDoMes(res.hoje) - 0.5) / ultimoDia(res.ano, Number(res.hoje.slice(5, 7)))) * 100 } : null
  const grupoRotulo = res?.agrupar === 'cargo' ? 'no mesmo cargo/função' : 'no total'

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', width: '100%' }}>
      <div className="modal-box" style={{ width: '100%', maxWidth: 680, maxHeight: 'none', boxShadow: '0 12px 32px -8px rgba(15,23,42,0.18)' }}>
        <div className="modal-header" style={{ background: 'linear-gradient(135deg, #1a6ea8, #14547f)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem' }}>
            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 34, borderRadius: 9, background: 'rgba(255,255,255,0.15)' }}>
              <CalendarDays size={18} color="#fff" />
            </span>
            <div>
              <h2>Calendário de Férias</h2>
              <div style={{ fontSize: '0.7rem', color: '#dbeafe', marginTop: 2 }}>Quem sai de férias em cada mês, ausentes ao mesmo tempo e prazo do aviso</div>
            </div>
          </div>
        </div>

        <div className="modal-corpo">
          <Linha label="Estabelecimento" icon={Building2}>
            <SeletorMultiplo opcoes={empresas.map(e => ({ cod: String(e.empcod), des: `${e.empcod} — ${e.empfan?.trim() || e.empnom?.trim()}` }))}
              selecionados={empresasSel} onChange={setEmpresasSel} placeholderBusca="Buscar empresa..." />
          </Linha>

          <Linha label="Filial" icon={MapPin}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
              <input value={filial} onChange={e => setFilial(e.target.value)} maxLength={2} style={{ maxWidth: 70 }} />
              <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>em branco = todas</span>
            </div>
          </Linha>

          <Linha label="Ano" icon={CalendarRange}>
            <select value={ano} onChange={e => setAno(Number(e.target.value))} style={{ maxWidth: 120 }}>
              {[anoAtual - 2, anoAtual - 1, anoAtual, anoAtual + 1].map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </Linha>

          <Linha label="Máx. ausentes" icon={Users}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
              <input type="number" min={1} value={limite} onChange={e => setLimite(Number(e.target.value))} style={{ maxWidth: 80 }} />
              <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>ao mesmo tempo, contando</span>
              <select value={agrupar} onChange={e => setAgrupar(e.target.value as 'cargo' | 'todos')} style={{ maxWidth: 200 }}>
                <option value="cargo">por cargo/função</option>
                <option value="todos">todos juntos</option>
              </select>
            </div>
          </Linha>

          {erro && (
            <div style={{ marginTop: '0.9rem', fontSize: '0.8rem', fontWeight: 600, padding: '0.55rem 0.9rem', borderRadius: 8, background: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca' }}>
              {erro}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-primary" onClick={gerar} disabled={carregando}>{carregando ? 'Gerando...' : 'Gerar'}</button>
        </div>
      </div>

      {res && (
        <div className="modal-box" style={{ width: '100%', maxWidth: 'none', maxHeight: 'none', boxShadow: '0 12px 32px -8px rgba(15,23,42,0.18)' }}>
          <div className="modal-corpo" style={{ display: 'flex', flexDirection: 'column', gap: '1.1rem' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              <span style={chip('#1e40af', '#dbeafe')}>Com férias em {res.ano}: {res.funcionarios.length} de {res.totalAtivos} ativos</span>
              <span style={chip('#1e40af', '#dbeafe')}>Programadas: {programadas.length}</span>
              <span style={res.conflitos.length ? chip('#991b1b', '#fee2e2') : chip('#166534', '#dcfce7')}>Acima do limite: {res.conflitos.length}</span>
              <span style={atrasados ? chip('#92400e', '#fef3c7') : chip('#166534', '#dcfce7')}>Aviso de 30 dias atrasado: {atrasados}</span>
            </div>

            {res.funcionarios.length === 0 && <div style={{ textAlign: 'center', color: '#94a3b8', padding: '1.2rem' }}>Nenhuma férias lançada em {res.ano} para os filtros escolhidos.</div>}

            {res.funcionarios.length > 0 && (
              <div style={{ overflowX: 'auto', border: '1px solid #bfdbfe', borderRadius: 8 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed', minWidth: 1100 }}>
                  <thead>
                    <tr>
                      <th style={{ ...th, width: 210 }}>Funcionário</th>
                      <th style={{ ...th, width: 150 }}>Cargo/Função</th>
                      {MESES.map(m => <th key={m} style={{ ...th, textAlign: 'center', padding: '0.5rem 0.2rem' }}>{m}</th>)}
                      <th style={{ ...th, width: 50, textAlign: 'right' }}>Dias</th>
                    </tr>
                  </thead>
                  <tbody>
                    {res.funcionarios.map((f, i) => (
                      <tr key={f.motcod} style={{ background: i % 2 ? '#f5f9fd' : '#fff' }}>
                        <td style={{ ...td, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={f.nome}>{f.nome}</td>
                        <td style={{ ...td, color: '#475569', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={f.cargo}>{f.cargo}</td>
                        {MESES.map((_, k) => (
                          <td key={k} style={{ ...td, padding: '0.3rem 0.15rem' }}>
                            <div style={{ position: 'relative', height: 16, background: '#eef2f7', borderRadius: 4, overflow: 'hidden' }}>
                              {f.gozos.map((g, n) => {
                                const t = trecho(g, res.ano, k + 1)
                                if (!t) return null
                                return <div key={n} title={`${dataBr(g.ini)} a ${dataBr(g.fim)} (${g.dias} dias${g.programado ? ', programado' : ''})`}
                                  style={{ position: 'absolute', top: 0, bottom: 0, left: `${t.left}%`, width: `${t.width}%`, background: g.programado ? '#2563eb' : '#64748b' }} />
                              })}
                              {marcaHoje && marcaHoje.mes === k + 1 && <div style={{ position: 'absolute', top: 0, bottom: 0, left: `${marcaHoje.pct}%`, width: 2, background: '#dc2626' }} />}
                            </div>
                          </td>
                        ))}
                        <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{f.diasNoAno}</td>
                      </tr>
                    ))}
                    <tr>
                      <td colSpan={2} style={{ ...td, fontWeight: 800, color: '#1b3f7b', background: '#eff6ff' }}>Pico de ausentes ao mesmo tempo ({grupoRotulo})</td>
                      {res.meses.map(m => (
                        <td key={m.mes} style={{ ...td, textAlign: 'center', fontWeight: 800, background: m.acima ? '#fee2e2' : '#eff6ff', color: m.acima ? '#991b1b' : '#1b3f7b' }}
                          title={`${m.pessoas} pessoa(s) com férias neste mês`}>{m.pico || ''}</td>
                      ))}
                      <td style={{ ...td, background: '#eff6ff' }} />
                    </tr>
                  </tbody>
                </table>
              </div>
            )}

            {res.funcionarios.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', fontSize: '0.74rem', color: '#475569', alignItems: 'center' }}>
                <span><span style={{ display: 'inline-block', width: 12, height: 12, background: '#64748b', borderRadius: 3, verticalAlign: 'middle' }} /> já tirou</span>
                <span><span style={{ display: 'inline-block', width: 12, height: 12, background: '#2563eb', borderRadius: 3, verticalAlign: 'middle' }} /> programada</span>
                {marcaHoje && <span><span style={{ display: 'inline-block', width: 12, height: 3, background: '#dc2626', verticalAlign: 'middle' }} /> hoje</span>}
              </div>
            )}

            {res.conflitos.length > 0 && (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontWeight: 800, color: '#991b1b', margin: '0 0 0.4rem' }}>
                  <AlertTriangle size={16} /> Ausências acima do limite de {res.limite} {grupoRotulo}
                </div>
                <div style={{ overflowX: 'auto', border: '1px solid #fecaca', borderRadius: 8 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead><tr>
                      <th style={th}>Período</th><th style={th}>Cargo/Função</th><th style={{ ...th, textAlign: 'right' }}>Ausentes</th><th style={th}>Funcionários</th>
                    </tr></thead>
                    <tbody>
                      {res.conflitos.map((c, i) => (
                        <tr key={i}>
                          <td style={{ ...td, whiteSpace: 'nowrap' }}>{dataBr(c.ini)}{c.fim !== c.ini ? ` a ${dataBr(c.fim)}` : ''}</td>
                          <td style={td}>{res.agrupar === 'cargo' ? c.grupo : 'Todos'}</td>
                          <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{c.pico}</td>
                          <td style={{ ...td, fontSize: '0.78rem', color: '#475569' }}>{c.nomes.join(', ')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {programadas.length > 0 && (
              <div>
                <div style={{ fontWeight: 800, color: '#1b3f7b', margin: '0 0 0.2rem' }}>Férias programadas e aviso de férias</div>
                <div style={{ fontSize: '0.76rem', color: '#6b7280', margin: '0 0 0.5rem', maxWidth: 760 }}>
                  O aviso de férias é um comunicado por escrito ao funcionário, que a empresa deve entregar com pelo menos 30 dias de antecedência (art. 135 da CLT),
                  com recibo assinado. "Aviso até" é a data-limite para entregar; se já passou, aparece como atrasado. Clique em <b>Gerar aviso</b> para imprimir o documento.
                </div>
                <div style={{ overflowX: 'auto', border: '1px solid #bfdbfe', borderRadius: 8 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead><tr>
                      <th style={th}>Funcionário</th><th style={th}>Cargo/Função</th><th style={th}>Início</th><th style={th}>Fim</th>
                      <th style={{ ...th, textAlign: 'right' }}>Dias</th><th style={th}>Aviso até</th><th style={th}>Situação</th><th style={th} />
                    </tr></thead>
                    <tbody>
                      {programadas.map(({ f, g }, i) => (
                        <tr key={i} style={{ background: i % 2 ? '#f5f9fd' : '#fff' }}>
                          <td style={{ ...td, fontWeight: 600 }}>{f.nome}</td>
                          <td style={{ ...td, color: '#475569' }}>{f.cargo}</td>
                          <td style={td}>{dataBr(g.ini)}</td>
                          <td style={td}>{dataBr(g.fim)}</td>
                          <td style={{ ...td, textAlign: 'right' }}>{g.dias}</td>
                          <td style={td}>{dataBr(g.avisoAte || '')}</td>
                          <td style={td}>
                            <span style={{ padding: '0.12rem 0.55rem', borderRadius: 999, fontSize: '0.7rem', fontWeight: 700, whiteSpace: 'nowrap',
                              color: g.avisoAtrasado ? '#92400e' : '#1e40af', background: g.avisoAtrasado ? '#fef3c7' : '#dbeafe' }}>
                              {g.avisoAtrasado ? 'Aviso atrasado' : 'Aviso no prazo'}
                            </span>
                          </td>
                          <td style={td}><button className="btn btn-secondary btn-sm" onClick={() => abrirAviso(f.motcod, g.ini)}>Gerar aviso</button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div style={{ fontSize: '0.72rem', color: '#6b7280' }}>
              Mostra os funcionários ativos com férias lançadas no cadastro (botão Férias) que caem no ano escolhido; início futuro conta como programada.
              O pico de cada mês é o maior número de ausentes no mesmo dia, {res.agrupar === 'cargo' ? 'contando separado por cargo/função' : 'somando todos os cargos'}; fica vermelho quando passa do limite.
              O aviso de férias deve ser entregue 30 dias antes do início: o sistema não sabe se foi entregue, só mostra a data limite e se ela já passou.
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
